
  import { defineComponent, PropType, provide, ref } from 'vue';
  import { mapActions } from 'vuex';
  import useVuelidate from '@vuelidate/core';

  import PageTitle from '@/common/components/redesigned/PageTitle.vue';
  import { AGGREGATED_TABLE_PDF_EXPORT_VIRTUALIZATION_KEY } from '@/common/components/redesigned/Table/constants/exportInjection';
  import constants from '@/common/constants';
  import { downloadPDF } from '@/common/utils/fileExport';
  import {
    buildAggregatedPagePdfTablesCloneFormatter,
    getPdfWideTableChunkMaxWidthForPrintPx,
  } from '@/common/utils/pdfSplitWideTablesForClone';

  import { ANALYTICS_NOTES_COMMON_ACTIONS_NAME } from '@/modules/ved/store/analyticsNotesCommon/name';

  import { exportExcelMonthly, exportExcelYearly } from '../../api/AnalyticsNotes';
  import { EXCEL_ERROR_MESSAGE, EXCEL_ERROR_TITLE } from '../../constants/error';
  import { TAnalyticsNotesFilter } from '../../types/analytics-notes';
  import { getMonthsYYYYMMFromPeriods, getYearsFromPeriods } from '../../utils/options';
  import VedExportButtons from '../common/VedExportButtons.vue';
  import AnalyticsNotesBaseInfo from './AnalyticsNotesBaseInfo.vue';
  import AnalyticsNotesFilter from './AnalyticsNotesFilter.vue';
  import AnalyticsNotesForeignTrade from './AnalyticsNotesForeignTrade.vue';
  import AnalyticsNotesTradeTabSwitcher from './AnalyticsNotesTradeTabSwitcher.vue';

  const normalizePeriods = (isMonthly: boolean, periods: string[]) => {
    return isMonthly
      ? getMonthsYYYYMMFromPeriods(periods)
      : getYearsFromPeriods(periods).map((yearValue) => String(yearValue));
  };

  export default defineComponent({
    name: 'AnalyticsNotesTemplate',
    components: {
      PageTitle,
      VedExportButtons,
      AnalyticsNotesBaseInfo,
      AnalyticsNotesFilter,
      AnalyticsNotesForeignTrade,
      AnalyticsNotesTradeTabSwitcher,
    },
    props: {
      defaultFilter: {
        type: Object as PropType<TAnalyticsNotesFilter>,
        required: true,
      },
      pageTitle: {
        type: String,
        required: true,
      },
      isMonthly: {
        type: Boolean,
        default: false,
      },
      exportPermissionCode: {
        type: Number,
      },
      readonlyPermissionCode: {
        type: Number,
      },
    },
    setup() {
      const isTableVirtualizationDisabledForPdfExport = ref(false);
      provide(AGGREGATED_TABLE_PDF_EXPORT_VIRTUALIZATION_KEY, isTableVirtualizationDisabledForPdfExport);
      return { isTableVirtualizationDisabledForPdfExport, v$: useVuelidate() };
    },
    data(): {
      filter: TAnalyticsNotesFilter;
      filterForExport: TAnalyticsNotesFilter;
      isExportProgress: boolean;
    } {
      return {
        filter: { ...this.defaultFilter },
        filterForExport: { ...this.defaultFilter },
        isExportProgress: false,
      };
    },
    computed: {
      normalizedFilter(): TAnalyticsNotesFilter {
        return {
          periods: normalizePeriods(this.isMonthly, this.filter.periods),
          country: Array.isArray(this.filter.country) ? this.filter.country : [],
          costType: this.filter.costType,
        };
      },
      normalizedRequestFilter(): TAnalyticsNotesFilter {
        return {
          periods: normalizePeriods(this.isMonthly, this.filter.periods),
          country: this.filter.country,
          costType: this.filter.costType,
        };
      },
      normalizedExportFilter(): TAnalyticsNotesFilter {
        return {
          periods: normalizePeriods(this.isMonthly, this.filterForExport.periods),
          country: this.filterForExport.country,
          costType: this.filterForExport.costType,
        };
      },
      getFileName() {
        return this.isMonthly
          ? 'Аналитическая_записка_по_внешней_торговле_месячная'
          : 'Аналитическая_записка_по_внешней_торговле_годовая';
      },
      getIsDisabledExportButtons() {
        return this.v$.$invalid;
      },
    },
    methods: {
      ...mapActions({
        searchCountryDeclensions: ANALYTICS_NOTES_COMMON_ACTIONS_NAME.searchCountryDeclensions,
      }),
      async ensureCountryDeclensionsLoaded(): Promise<void> {
        const selectedCountryCode = String(this.normalizedRequestFilter.country ?? '').trim();
        if (!selectedCountryCode) return;

        this.searchCountryDeclensions({ countryCode: selectedCountryCode });
      },
      searchFilter(updatedFilter: Partial<TAnalyticsNotesFilter>) {
        this.filter = { ...this.filter, ...updatedFilter };
      },
      updateFilterForExport(filter: Partial<TAnalyticsNotesFilter>): void {
        this.filterForExport = { ...this.filterForExport, ...filter };
      },

      async exportExcel() {
        const request = this.normalizedExportFilter;
        this.isExportProgress = true;

        const promise = this.isMonthly
          ? exportExcelMonthly(request, this.getFileName)
          : exportExcelYearly(request, this.getFileName);

        promise
          .catch(() => {
            constants.alert.fire(EXCEL_ERROR_TITLE, EXCEL_ERROR_MESSAGE, 'error');
          })
          .finally(() => {
            this.isExportProgress = false;
          });
      },
      async printPage() {
        if (this.isExportProgress) return;
        this.isExportProgress = true;

        try {
          this.isTableVirtualizationDisabledForPdfExport = true;
          await this.$nextTick();
          await new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
          });
          const pdfPromise = downloadPDF({
            clonedElementClassName: 'analytics-notes__export-pdf',
            fileName: this.getFileName,
            pagebreakMode: 'row-friendly',
            clonedNodeFormatter: buildAggregatedPagePdfTablesCloneFormatter({
              tableSelector: '.analytics-notes__print table',
              withTopBorder: false,
              maxWidthPx: getPdfWideTableChunkMaxWidthForPrintPx(),
            }),
            chunkedCanvasRender: {
              enabled: true,
              maxCanvasHeightPx: 7000,
            },
          });

          this.isTableVirtualizationDisabledForPdfExport = false;
          await pdfPromise;
        } finally {
          this.isExportProgress = false;
        }
      },
    },
    watch: {
      normalizedRequestFilter: {
        handler() {
          this.ensureCountryDeclensionsLoaded();
        },
        deep: true,
        immediate: true,
      },
    },
  });
